<template>
  <header class="pageHeader">
    <div class="container">
      <!-- desktop nav -->
      <div class="row no-gutters desktopNav">
        <!-- back to other system -->
        <div class="col-md-3 backLink">
          <a v-if="backLink.isBacklink" :href="backLink.backLinkUrl">
            <svg
              viewBox="0 0 6 8"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <defs>
                <path
                  :id="randomId"
                  d="M5.86192881,8.86192881 C6.12227834,8.60157928 6.54438833,8.60157928 6.80473785,8.86192881 C7.06508738,9.12227834 7.06508738,9.54438833 6.80473785,9.80473785 L3.47140452,13.1380712 C3.21105499,13.3984207 2.78894501,13.3984207 2.52859548,13.1380712 L-0.804737854,9.80473785 C-1.06508738,9.54438833 -1.06508738,9.12227834 -0.804737854,8.86192881 C-0.544388326,8.60157928 -0.12227834,8.60157928 0.138071187,8.86192881 L3,11.7238576 L5.86192881,8.86192881 Z"
                />
              </defs>
              <g
                id="Symbols"
                stroke="none"
                stroke-width="1"
                fill="none"
                fill-rule="evenodd"
              >
                <g
                  id="navigation/centrum_bezpecnosti/sk/01_lg_no_back_to_george"
                  transform="translate(-32.000000, -36.000000)"
                >
                  <g id="Group" transform="translate(32.000000, 29.000000)">
                    <mask id="mask-2" fill="white">
                      <use
                        :href="`#${randomId}`"
                        :xlink:href="`#${randomId}`"
                      />
                    </mask>
                    <use
                      id="Path"
                      fill="#FFFFFF"
                      fill-rule="nonzero"
                      transform="translate(3.000000, 11.000000) rotate(90.000000) translate(-3.000000, -11.000000) "
                      :xlink:href="`#${randomId}`"
                    />
                  </g>
                </g>
              </g>
            </svg>
            <span>{{ backLink.backLinkText }}</span>
          </a>
        </div>

        <!-- title -->
        <div class="col-md-6 title">
          <router-link :to="{ name: 'dashboard' }">
            {{ t('pageTitle') }}
          </router-link>
        </div>

        <!-- icon nav and logout -->
        <div class="col-md-3 logoutAndSettings">
          <router-link
            class="homeNav"
            :title="t('interface.navBack')"
            :to="{ name: 'dashboard' }"
          >
            <svg class="ico">
              <use href="#ico-home" xlink:href="#ico-home" />
            </svg>
          </router-link>

          <!-- settings -->
          <div
            class="settings"
            :class="{ opened: header.settingsOpened }"
            aria-haspopup="true"
            :aria-expanded="`${header.settingsOpened}`"
          >
            <svg
              role="button"
              tabindex="0"
              class="ico tooltipItem"
              :aria-label="t('interface.openSettings')"
              @blur="handleSettingsBlur"
              @click.prevent="header.settingsOpened = !header.settingsOpened"
              @keydown.space.prevent="
                header.settingsOpened = !header.settingsOpened
              "
              @keydown.escape.prevent="header.settingsOpened = false"
            >
              <use href="#ico-settings" xlink:href="#ico-settings" />
            </svg>
            <div
              v-if="header.settingsOpened"
              role="menu"
              class="header-tooltip"
              @click="header.settingsOpened = true"
              @keydown.escape.prevent="header.settingsOpened = false"
            >
              <ElemLangSelect />
            </div>
          </div>

          <!-- logout -->
          <button
            v-if="isLoggedIn"
            class="btn btn-medium btn-icon btn-logout"
            @click="logout"
          >
            <svg class="ico-logout" aria-hidden="true" viewBox="0 0 512 512">
              <use xlink:href="#ico-logout" href="#ico-logout" />
            </svg>
            <span>{{ t('interface.btnLogout') }}</span>
          </button>
          <button
            v-else
            class="btn btn-medium btn-icon btn-logout"
            @click="login"
          >
            <svg class="ico-logout" aria-hidden="true" viewBox="0 0 512 512">
              <use xlink:href="#ico-logout" href="#ico-logout" />
            </svg>
            <span>{{ t('interface.btnLogin') }}</span>
          </button>
        </div>
      </div>

      <!-- mobile nav -->
      <div class="row no-gutters mobileNav">
        <!-- burger menu -->
        <div class="col-xs-5 menu">
          <!-- menu link -->
          <a
            id="mobileNav"
            href="javascript: void(0);"
            :class="{ active: mobileNavOpened }"
            role="button"
            aria-haspopup="true"
            aria-expanded="false"
            @click="stateMobileNavOpened = !mobileNavOpened"
            @keypress.space.prevent="stateMobileNavOpened = !mobileNavOpened"
            @keydown.escape.prevent="stateMobileNavOpened = false"
          >
            <svg class="ico">
              <use href="#ico-menu" xlink:href="#ico-menu" />
            </svg>
            <span>Menu</span>
          </a>
        </div>

        <!-- mobile menu container -->
        <div
          v-if="mobileNavOpened"
          aria-labelledby="mobileNav"
          class="mobileNavContainer"
          @keydown.escape.prevent="stateMobileNavOpened = false"
        >
          >
          <!-- navigation -->
          <div class="links">
            <a v-if="backLink.isBacklink" :href="backLink.backLinkUrl">
              {{ backLink.backLinkText }}
            </a>
            <router-link :to="{ name: 'dashboard' }">
              {{ t('interface.home') }}
            </router-link>
          </div>

          <!-- separator -->
          <div class="separator" />

          <!-- language -->
          <ElemLangSelect />
        </div>

        <!-- logo -->
        <div class="col-xs-2 logo">
          <router-link
            :title="t('interface.navBack')"
            :to="{ name: 'dashboard' }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="34"
              height="24"
              viewBox="0 0 34 24"
            >
              <path
                fill="#FFF"
                d="M17.695,21.425 L17.695,2.175 C17.695,1.16 18.01,0.6 19.06,0.6 L24.94,0.6 C29.91,0.6 32.535,2.63 32.535,6.48 C32.535,8.825 31.485,10.575 29,11.415 C32.01,12.185 33.41,14.11 33.41,16.875 C33.41,20.865 30.82,23 25.815,23 L19.06,23 C18.01,23 17.695,22.44 17.695,21.425 Z M20.32,20.655 L25.71,20.655 C28.58,20.655 30.75,19.745 30.75,16.63 C30.75,13.655 28.58,12.64 25.71,12.64 L20.32,12.64 L20.32,20.655 Z M20.32,10.365 L25.185,10.365 C27.845,10.365 29.84,9.49 29.84,6.655 C29.84,3.82 27.67,2.98 24.8,2.98 L20.32,2.98 L20.32,10.365 Z M0.715,11.8 C0.715,4.03 3.585,0.425 9.85,0.425 C14.05,0.425 15.135,0.67 15.135,2.105 L15.135,2.84 L9.85,2.84 C5.405,2.84 3.445,4.975 3.445,11.8 C3.445,18.625 5.405,20.76 9.85,20.76 L15.135,20.76 L15.135,21.495 C15.135,22.93 14.05,23.175 9.85,23.175 C3.585,23.175 0.715,19.57 0.715,11.8 Z"
              />
            </svg>
          </router-link>
        </div>

        <!-- logout button -->
        <div class="col-xs-5 logout">
          <!-- logout -->
          <button
            class="btn btn-medium btn-logout btn-icon"
            @click.prevent="logout()"
          >
            <svg class="ico-logout" aria-hidden="true" viewBox="0 0 512 512">
              <use xlink:href="#ico-logout" href="#ico-logout" />
            </svg>
            <span v-if="isLoggedIn">
              {{ t('interface.btnLogout') }}
            </span>
            <span v-else>{{ t('interface.btnLogin') }}</span>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink } from 'vue-router';

import ApiCalls from '@/classes/ApiCalls';
import Helpers from '@/classes/Helpers';
import ElemLangSelect from '@/components/elements/ElemLangSelect.vue';
import auth from '@/middleware/auth';
import router from '@/router';
import global from '@/store/global';
import header from '@/store/header';
import { BackLinkT } from '@/support/types';

const { t } = useI18n();

// defines route and text for back link
const backLink = ref<BackLinkT>({
  isBacklink: false,
  backLinkText: '',
  backLinkUrl: '',
});
const randomId: string = Helpers.generateRandomId();
const apiCalls = new ApiCalls();

const isLoggedIn = computed(() => {
  return global.authToken !== '';
});

const mobileNavOpened = computed(() => {
  return header.mobileNavOpened;
});

watch(
  () => global.referer,
  () => {
    setHeaderBackLink();
  },
);

watch(
  () => global.language,
  () => {
    setHeaderBackLink();
  },
);

watch(
  () => global.idToken,
  () => {
    setHeaderBackLink();
  },
);

onMounted(() => {
  setHeaderBackLink();
});

// invalidate token and redirect it to slsp logout page
const logout = () => {
  apiCalls.invalidateToken(true);
};

// METHODS
// blur out of dropdown
const handleSettingsBlur = () => {
  // if its without small timeout, blur jumps before selection and correct item is not selected
  setTimeout(() => {
    if (!(document.activeElement as any).classList.contains('tooltipItem')) {
      header.settingsOpened = false;
    }
  }, 100);
};

const setGeorgeProfileBackLink = (
  type: 'blue' | 'green' | 'old' | 'default',
) => {
  backLink.value.isBacklink = true;
  backLink.value.backLinkText = t('interface.btnBackToGeorge').toString();

  let localeBackLink = import.meta.env.VITE_APP_GEORGE_PROFILE_URL as string;

  if (type === 'green' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_GREEN) {
    localeBackLink = import.meta.env
      .VITE_APP_GEORGE_PROFILE_URL_GREEN as string;
  }

  if (type === 'blue' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_BLUE) {
    localeBackLink = import.meta.env.VITE_APP_GEORGE_PROFILE_URL_BLUE as string;
  }

  if (type === 'old' && import.meta.env.VITE_APP_GEORGE_PROFILE_URL_OLD) {
    localeBackLink = import.meta.env.VITE_APP_GEORGE_PROFILE_URL_OLD as string;
  }

  backLink.value.backLinkUrl = localeBackLink;
};

const setHeaderBackLink = () => {
  // setup backlinks
  switch (global.referer) {
    case 'gbdb5yhx3oljh7gd': // george business
      backLink.value.isBacklink = true;
      backLink.value.backLinkText = t(
        'interface.btnBackToGeorgeBusiness',
      ).toString();
      backLink.value.backLinkUrl = `${
        import.meta.env.VITE_APP_GEORGE_BUSINESS_URL as string
      }?id_token=${global.idToken}`;
      break;
    case 'gbgbhj6uozx35jty': // george business green
      backLink.value.isBacklink = true;
      backLink.value.backLinkText = t(
        'interface.btnBackToGeorgeBusiness',
      ).toString();
      backLink.value.backLinkUrl = `${
        import.meta.env.VITE_APP_GEORGE_BUSINESS_URL_GREEN as string
      }?id_token=${global.idToken}`;
      break;
    case 'gbblua4h9iow27jn': // george business blue
      backLink.value.isBacklink = true;
      backLink.value.backLinkText = t(
        'interface.btnBackToGeorgeBusiness',
      ).toString();
      backLink.value.backLinkUrl = `${
        import.meta.env.VITE_APP_GEORGE_BUSINESS_URL_BLUE as string
      }?id_token=${global.idToken}`;
      break;
    case 'ghfkqedbnneqsjvt': // george
      backLink.value.isBacklink = true;
      backLink.value.backLinkText = t('interface.btnBackToGeorge').toString();
      backLink.value.backLinkUrl = import.meta.env
        .VITE_APP_GEORGE_URL as string;
      break;
    case 'gyfr21xk31nbc7ou': // george profile
      setGeorgeProfileBackLink('old');
      break;
    case 'gpboclmi17hzsekh': // george profile
      setGeorgeProfileBackLink('blue');
      break;
    case 'gpgpojwpp6kifu9h': // george profile
      setGeorgeProfileBackLink('green');
      break;
    case 'gpdg91ictbpmqfou': // george profile
      setGeorgeProfileBackLink('default');
      break;
    case 'bn63pzfvu3mf8ryp': // b24
      backLink.value.isBacklink = true;
      backLink.value.backLinkText = t('interface.btnBackToB24').toString();
      backLink.value.backLinkUrl = import.meta.env.VITE_APP_B24_URL as string;
      break;
    default:
      backLink.value.isBacklink = false;
      backLink.value.backLinkText = '';
      backLink.value.backLinkUrl = '';
      break;
  }
};

const login = (): void => {
  const routeDetails = router.currentRoute;
  auth({ to: { ...routeDetails } });
};
</script>

<style lang="scss">
@use '@/assets/scss/colors.scss';
@use '@/assets/scss/variables.scss';
@use '@/assets/scss/mixins.scss';

.pageHeader {
  position: fixed;
  top: 0;
  z-index: 5;

  width: 100%;
  height: mixins.calculateRem(variables.$headerHeight);
  box-shadow: 0 -2px 4px 0 rgba(0, 0, 0, 0.2);

  background-color: colors.$eg-ocean;

  .container {
    position: relative;
    height: 100%;

    .row {
      align-items: center;
      height: inherit;

      // DESKTOP NAV
      .backLink,
      .title,
      .logoutAndSettings {
        height: 100%;
        display: flex;
        align-items: center;
      }

      .backLink {
        a {
          display: flex;

          justify-content: center;
          align-items: center;

          @include mixins.fontSize(16px);
          @include mixins.lineHeight(0px);
          text-decoration: none;
          color: colors.$g-white;
          font-weight: 300;

          svg {
            width: 6px;
            height: 8px;
          }

          span {
            margin-left: mixins.calculateRem(10px);
            // padding-top: mixins.calculateRem(5px);
          }
        }
      }

      .title {
        justify-content: center;

        a {
          @include mixins.fontSize(24px);
          @include mixins.lineHeight(0px);
          text-decoration: none;
          color: colors.$g-white;
          font-weight: 300;
        }
        // margin-right: auto; // move it to left #justflexthings
      }

      .logoutAndSettings {
        justify-content: flex-end;

        .homeNav {
          line-height: 0;
        }

        .settings {
          position: relative;

          padding: 0 mixins.calculateRem(25px);

          line-height: 0;

          &.opened {
            svg.ico {
              opacity: 0.7;
              transition: opacity 0.3s;
            }
          }
        }
      }

      svg.ico {
        cursor: pointer;

        width: mixins.calculateRem(24px);
        height: mixins.calculateRem(24px);
        fill: colors.$g-white;

        transition: opacity 0.3s;

        &:hover {
          opacity: 0.7;
          transition: opacity 0.3s;
        }
      }

      // MOBILE NAV
      .menu,
      .logo,
      .logout {
        height: 100%;
        display: flex;
        align-items: center;
      }

      .menu {
        position: relative;

        a {
          display: flex;
          justify-content: center;
          align-items: center;

          color: colors.$g-white;
          text-decoration: none;
          @include mixins.fontSize(16px);
          line-height: 0;
          font-weight: 300;

          span {
            margin-left: mixins.calculateRem(
              variables.$spacingHeaderContent * 0.25
            );
          }

          &.active,
          &:hover {
            color: rgba(colors.$g-white, 0.7);

            svg {
              fill: rgba(colors.$g-white, 0.7);
            }
          }
        }
      }

      .mobileNavContainer {
        position: absolute;
        top: 96%;
        left: 50%;
        transform: translateX(-50%);
        z-index: 100;

        padding: mixins.calculateRem(variables.$spacingHeaderContent)
          mixins.calculateRem(variables.$spacingHeaderContent * 0.5);

        width: 100%;
        height: auto;

        border-radius: variables.$border-radius;

        background-color: colors.$eg-grey-light;

        box-shadow: 0 10px 30px 5px rgba(0, 0, 0, 0.2);
        animation: mobileMenuReveal variables.$animSpeed * 0.5 ease-in-out
          forwards;
        @keyframes mobileMenuReveal {
          0% {
            top: 106%;
            box-shadow: none;
          }
          100% {
            top: 96%;
            box-shadow: 0 10px 30px 5px rgba(0, 0, 0, 0.2);
          }
        }

        &:before {
          content: ' ';

          position: absolute;
          bottom: 100%;
          left: 16px;

          height: 0;
          width: 0;

          pointer-events: none;

          border: solid transparent;
          border-width: 11px;
          border-bottom-color: colors.$eg-grey-light;
        }

        .links {
          display: flex;
          flex-direction: column;

          a {
            @include mixins.fontSize(16px);
            @include mixins.lineHeight(26px);
            color: colors.$g-primary-900;
            font-weight: 600;
            text-decoration: none;

            padding: mixins.calculateRem(7px) 0;
          }
        }
      }

      .logo {
        justify-content: center;
        a {
          line-height: 0;
        }
      }

      .logout {
        justify-content: flex-end;
      }

      // MOBILE MENU
    }

    .desktopNav {
      display: none;
      @include mixins.breakpoint('lg') {
        display: flex;
      }
    }

    .mobileNav {
      display: flex;
      @include mixins.breakpoint('lg') {
        display: none;
      }
    }
  }

  .header-tooltip {
    position: absolute;
    z-index: 100;
    top: mixins.calculateRem(45px);
    left: 50%;
    transform: translateX(-50%);

    width: mixins.calculateRem(300px);

    background-color: colors.$g-white;
    padding: mixins.calculateRem(10px) mixins.calculateRem(15px);

    border-radius: variables.$border-radius;
    box-shadow: 0 10px 30px 5px rgba(0, 0, 0, 0.2);

    @include mixins.fontSize(14px);
    @include mixins.lineHeight(24px);

    animation: tooltipReveal variables.$animSpeed * 0.5 ease-in-out forwards;

    @keyframes tooltipReveal {
      0% {
        box-shadow: none;
        top: mixins.calculateRem(50px);
      }
      100% {
        top: mixins.calculateRem(45px);
        box-shadow: 0 10px 30px 5px rgba(0, 0, 0, 0.2);
      }
    }

    // top arrow
    &:before {
      content: ' ';

      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);

      height: 0;
      width: 0;

      pointer-events: none;

      border: solid transparent;
      border-width: 8px;
      border-bottom-color: colors.$g-white;
    }
  }
}
</style>
